import React, { useState, useEffect, useCallback } from "react";

import { Grid, Typography, Button, Paper } from "@mui/material";

import { useTranslation } from "react-i18next";
import { useDialogContext, DialogActionTypes } from "../context/DialogContext";
import { useBackend } from "../context/BackendContext";
import { ResponseCode } from "../enums/ResponseCode";
import { LanguageConverter } from "../enums/LanguageConverter";
import DeviceBlacklistTable from "../components/Tables/DeviceBlacklistTable";

export default function DeviceBlacklist() {
  const dialogDispatch = useDialogContext();
  const backend = useBackend();
  const { t, i18n } = useTranslation();
  const [error, setError] = useState("");
  const [tableData, setTableData] = useState([]);

  const refreshTable = useCallback(
    (backend) => {
      backend.bckDevicesListDeviceBlacklist(LanguageConverter[i18n.language]).then((json) => {
        if (json.code === 0) {
          setTableData((prev) => {
            return json.content.list;
          });
        } else if (json.code === ResponseCode.ACCESS_DENIED) {
          setError(t("AUTH_ERROR"));
        } else {
          setError(t("REQUEST_ERROR"));
        }
      });
    },
    [t, i18n.language]
  );

  useEffect(() => {
    refreshTable(backend);
  }, [backend, refreshTable]);

  const handleCreateDeviceBlacklist = () => {
    dialogDispatch({
        type: DialogActionTypes.SimpleTextDialogOpen,
        initialValue: "",
        textLabel: t("Device ID"),
        title: t("Add device"),
        onChange: (e) => {
          let filteredValue = e.target.value.replace(/[^0-9]/g, "");
          return filteredValue.substr(0, 10);
        },
        handleConfirm: (text) => {
          let parsedText = parseInt(text);
          if (parsedText) {
            backend.bckDevicesCreateOrUpdateDeviceBlacklist({ devId: parsedText }).then((json) => {
              if (json.code === 0) {
                let aliasId = json.content;
                let newElement = {
                  id: aliasId,
                  devId: parsedText
                };
                setTableData((prev) => {
                  let newArr = [newElement, ...prev];
                  return newArr;
                });
              } else if (json.code === 3) {
                setError(json.message);
              } else {
                setError(t("REQUEST_ERROR"));
              }
            });
          }
        }
    });
  };

  const handleRemoveDevice = (entry) => {
    dialogDispatch({
      type: DialogActionTypes.ConfirmationDialogOpen,
      userMessage: t("Remove device?", { title: entry.devId }),
      handleConfirm: () => {
        backend.bckDevicesRemoveDeviceBlacklist(entry.id).then((json) => {
          if (json.code === 0) {
            setTableData((prev) => {
              let foundIndex = prev.findIndex((x) => x.id === entry.id);
              if (foundIndex !== -1) {
                prev.splice(foundIndex, 1);
              }
              return [...prev];
            });
          } else if (json.code === 3) {
            setError(t(json.message));
          } else {
            setError(t("REQUEST_ERROR"));
          }
        });
      },
    });
  };

  const handleEditReason = (entry) => {
    dialogDispatch({
        type: DialogActionTypes.SimpleTextDialogOpen,
        initialValue: entry.reason,
        textLabel: t("Reason"),
        title: t("Edit reason"),
        maxLength: 512,
        handleConfirm: (text) => {
          let newEntry = { ...entry, reason: text };
          backend.bckDevicesCreateOrUpdateDeviceBlacklist(newEntry, LanguageConverter[i18n.language]).then((json) => {
            if (json.code === 0) {
              setTableData((prev) => {
                let newArr = prev.map((e) => (e.id === newEntry.id ? newEntry : e));
                return newArr;
              });
            } else if (json.code === 3) {
              setError(json.message);
            } else {
              setError(t("REQUEST_ERROR"));
            }
          });
        }
    });
  };

  return (
    <>
      <Paper style={{ overflow: "auto", padding: "24px", maxHeight: "85vh" }}>
        <Grid container direction="column" spacing={2}>
          <Typography variant="h5" color="textSecondary">
            {t("Device blacklist")}
          </Typography>
          <Typography color="secondary">{error}</Typography>
          <Grid item>
            <Button
              onClick={handleCreateDeviceBlacklist}
              variant="contained"
              color="primary"
            >
              {t("Add device")}
            </Button>
          </Grid>
          <div style={{ maxHeight: "70vh", overflow: "auto" }}>
            <DeviceBlacklistTable
              data={tableData}
              handleEditReason={handleEditReason}
              handleRemove={handleRemoveDevice}
            />
          </div>
        </Grid>
      </Paper>
    </>
  );
}
